import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  CircularProgress,
  TextField,
  Grow,
} from "@mui/material";
import {
  CancelFormActionButton,
  PrimaryFormActionButton,
} from "../../../Features/Table/GenericDraggableTable/TableStyles";
import { createOrgLocationMaster, updateOrgLocationMaster } from "../../../Api/RbacAdminApi";
import CODE from "../../../Static/Constants/StatusCodes";
import { useToast } from "../../../Hooks/useToast";

const PLACEHOLDER_MAP = {
  country: "Enter country name",
  zone: "Enter zone",
  state: "Enter state name",
  city: "Enter city name",
  depot: "Enter depot name",
};

const getPlaceholder = (type) => {
  const key = (type || "").toLowerCase();
  return PLACEHOLDER_MAP[key] || `Enter ${key || "value"}`;
};

// Renders text with the matching substring subtly bolded.
// Uses native <span> with inline style so it sits inside a single flex item
// without splitting into multiple flex children.
const HighlightMatch = ({ text, query }) => {
  const i = text.toLowerCase().indexOf((query || "").toLowerCase());
  if (!query || i === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, i)}
      <span style={{ fontWeight: 600, color: "#6366F1" }}>
        {text.slice(i, i + query.length)}
      </span>
      {text.slice(i + query.length)}
    </>
  );
};

// ─── Suggestion panel ────────────────────────────────────────────────────────
// Always rendered "connected" — snapped flush to the input with no gap.
// Input bottom-radius is flattened by FormField when this panel is open.

const SuggestionPanel = ({ suggestions, activeIdx, query, onPick }) => {
  const hasSuggestions = suggestions.length > 0;

  return (
    <Box
      sx={{
        position: "absolute",
        // Flush against the input — no gap so they appear as one component
        top: "100%",
        left: 0,
        right: 0,
        zIndex: 1400,
        backgroundColor: "#fff",
        border: "1.5px solid #D1D5DB",
        // No top border — input's bottom border is the shared divider
        borderTop: "none",
        borderRadius: "0 0 10px 10px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.07), 0 12px 28px rgba(0,0,0,0.07)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          maxHeight: "244px",
          overflowY: "auto",
          py: "4px",
          "&::-webkit-scrollbar": { width: "3px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": { background: "#E2E8F0", borderRadius: "2px" },
          "&::-webkit-scrollbar-thumb:hover": { background: "#CBD5E1" },
          scrollbarWidth: "thin",
          scrollbarColor: "#E2E8F0 transparent",
        }}
      >
        {hasSuggestions ? (
          suggestions.map((s, i) => (
            <Box
              key={s}
              onMouseDown={(e) => {
                e.preventDefault(); // keep TextField focused
                onPick(s);
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "9px",
                px: "10px",
                py: "7px",
                mx: "4px",
                borderRadius: "7px",
                cursor: "pointer",
                backgroundColor: i === activeIdx ? "#EEEFFE" : "transparent",
                transition: "background-color 0.1s ease",
                "&:hover": { backgroundColor: i === activeIdx ? "#EEEFFE" : "#F5F6FA" },
              }}
            >
              {/*
               * Wrapping HighlightMatch in a <span> is critical.
               * Without it the React fragment's children become separate flex
               * items and the row gap splits words apart visually.
               */}
              <Box
                component="span"
                sx={{
                  fontSize: 13,
                  fontFamily: "Roboto",
                  color: i === activeIdx ? "#4446C4" : "#1F2937",
                  lineHeight: "20px",
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  transition: "color 0.1s ease",
                }}
              >
                <HighlightMatch text={s} query={query} />
              </Box>
            </Box>
          ))
        ) : (
          // Empty state — only shown when no match at all (not on exact-match).
          <Box sx={{ px: "14px", py: "10px", textAlign: "center" }}>
            <Typography
              sx={{ fontSize: 12.5, color: "#94A3B8", fontFamily: "Roboto", lineHeight: "18px" }}
            >
              No matching locations found
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ─── Form field ───────────────────────────────────────────────────────────────

const FormField = ({
  label,
  value,
  onChange,
  onBlur,
  onSelect,
  required,
  placeholder,
  autoFocus,
  error,
  helperText,
  enableSuggestions = false,
  suggestions = [],
  // true when the trimmed value is already an exact match in existing data
  exactMatchExists = false,
}) => {
  const [panelOpen, setPanelOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);

  // Three-case panel logic:
  //  • suggestions present → open (show list)
  //  • no suggestions + exact match → CLOSED (user already typed a valid value)
  //  • no suggestions + no match → open (show "No matching locations found")
  const showPanel =
    enableSuggestions &&
    panelOpen &&
    (value || "").trim().length >= 1 &&
    (suggestions.length > 0 || !exactMatchExists);

  const handleKeyDown = (e) => {
    if (!showPanel) return;
    const count = suggestions.length;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, count - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter" && activeIdx >= 0 && count > 0) {
      e.preventDefault();
      onSelect?.(suggestions[activeIdx]);
      setPanelOpen(false);
      setActiveIdx(-1);
    } else if (e.key === "Escape") {
      setPanelOpen(false);
      setActiveIdx(-1);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "7px", position: "relative" }}>
      {/* Label */}
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 600,
          color: "#1F2937",
          lineHeight: "18px",
          fontFamily: "Roboto",
          letterSpacing: "0.01em",
        }}
      >
        {label}
        {required && (
          <Box component="span" sx={{ color: "#EF4444", ml: "3px" }}>
            *
          </Box>
        )}
      </Typography>

      {/* Input */}
      <TextField
        value={value}
        onChange={(e) => {
          onChange(e);
          if (enableSuggestions) {
            setPanelOpen(true);
            setActiveIdx(-1);
          }
        }}
        onFocus={() => {
          if (enableSuggestions && value) setPanelOpen(true);
        }}
        onBlur={() => {
          // Fires on real focus-loss only; suggestion onMouseDown uses
          // preventDefault so clicking a suggestion never triggers onBlur.
          setPanelOpen(false);
          setActiveIdx(-1);
          onBlur?.();
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoFocus={autoFocus}
        variant="outlined"
        fullWidth
        autoComplete="off"
        error={error}
        helperText={error ? helperText : undefined}
        inputProps={{
          style: {
            padding: "10px 14px",
            fontSize: 14,
            fontFamily: "Roboto",
            color: "#111827",
            height: "24px",
            lineHeight: "24px",
          },
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            // Flatten bottom corners when panel is attached so they read as one unit
            borderRadius: showPanel ? "8px 8px 0 0" : "8px",
            backgroundColor: "#fff",
            transition: "box-shadow 0.18s ease, border-radius 0.1s ease",
            "& fieldset": {
              borderColor: "#D1D5DB",
              borderWidth: "1.5px",
              transition: "border-color 0.18s ease",
            },
            "&:hover fieldset": { borderColor: "#9CA3AF" },
            "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(109,114,246,0.14)" },
            "&.Mui-focused fieldset": { borderColor: "#6D72F6", borderWidth: "1.5px" },
            "&.Mui-error fieldset": { borderColor: "#F87171", borderWidth: "1.5px" },
            "&.Mui-error:hover fieldset": { borderColor: "#EF4444" },
            "&.Mui-error.Mui-focused": { boxShadow: "0 0 0 3px rgba(239,68,68,0.12)" },
            "&.Mui-error.Mui-focused fieldset": { borderColor: "#EF4444" },
          },
          "& .MuiFormHelperText-root.Mui-error": {
            fontSize: 12,
            fontFamily: "Roboto",
            margin: "5px 0 0 0",
            lineHeight: "16px",
            color: "#EF4444",
          },
        }}
      />

      {/* Floating suggestion panel */}
      {showPanel && (
        <SuggestionPanel
          suggestions={suggestions}
          activeIdx={activeIdx}
          query={value}
          onPick={(val) => {
            onSelect?.(val);
            setPanelOpen(false);
            setActiveIdx(-1);
          }}
        />
      )}
    </Box>
  );
};

// ─── Scoring helper for smarter suggestion ranking ────────────────────────────

// Returns 3 = starts-with, 2 = word-start, 1 = contains, 0 = no match
const scoreSuggestion = (val, query) => {
  const v = val.toLowerCase();
  const q = query.toLowerCase();
  if (v.startsWith(q)) return 3;
  if (v.split(/[\s\-_]+/).some((w) => w.startsWith(q))) return 2;
  if (v.includes(q)) return 1;
  return 0;
};

// ─── Main dialog ──────────────────────────────────────────────────────────────

const CreateEditLocationDialog = ({
  open,
  mode,
  location,
  locationLevels,
  locations = [],
  onClose,
  onSuccess,
}) => {
  const { openNotification } = useToast();
  const [capturedMode, setCapturedMode] = useState(mode);
  const isEdit = capturedMode === "edit";
  const [form, setForm] = useState({});
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (open) {
      setCapturedMode(mode);
      const initial = {};
      locationLevels.forEach((lvl) => {
        initial[lvl.level] = mode === "edit" && location ? location[lvl.level] || "" : "";
      });
      setForm(initial);
      setTouched({});
      setErrors({});
      setFormError("");
    }
  }, [open, mode, location, locationLevels]);

  const getFieldValidationError = useCallback((lvl, value) => {
    const trimmed = (value || "").trim();
    return trimmed ? "" : `${lvl.type} is required`;
  }, []);

  // Returns true when the trimmed input value exactly matches an existing record.
  // Used to suppress the "no match" empty-state when the user has already
  // typed a known valid value.
  const fieldHasExactMatch = useCallback(
    (lvl, value) => {
      if (lvl.isUnique) return false;
      const trimmed = (value || "").trim();
      if (!trimmed) return false;
      const lower = trimmed.toLowerCase();
      return (locations || []).some(
        (loc) => (loc[lvl.level] || "").trim().toLowerCase() === lower
      );
    },
    [locations]
  );

  // Suggestions disabled for isUnique fields to avoid confusing duplicate-UX.
  // Non-unique fields get up to 7 ranked suggestions (word-start > contains).
  const getFieldSuggestions = useCallback(
    (lvl, value) => {
      if (lvl.isUnique) return [];
      const trimmed = (value || "").trim();
      if (trimmed.length < 1) return [];
      const seen = new Set();
      const scored = [];
      for (const loc of locations || []) {
        const val = (loc[lvl.level] || "").trim();
        if (!val || seen.has(val)) continue;
        if (val.toLowerCase() === trimmed.toLowerCase()) continue; // skip exact match
        const score = scoreSuggestion(val, trimmed);
        if (score > 0) {
          seen.add(val);
          scored.push({ val, score });
        }
      }
      return scored
        .sort((a, b) => b.score - a.score)
        .slice(0, 7)
        .map((x) => x.val);
    },
    [locations]
  );

  const handleChange = (lvl, idx, value) => {
    setFormError("");
    setForm((prev) => ({ ...prev, [lvl.level]: value }));
    if (touched[lvl.level]) {
      setErrors((prev) => ({
        ...prev,
        [lvl.level]: getFieldValidationError(lvl, value),
      }));
    }
  };

  const handleBlur = (lvl, idx) => {
    setTouched((prev) => ({ ...prev, [lvl.level]: true }));
    setErrors((prev) => ({
      ...prev,
      [lvl.level]: getFieldValidationError(lvl, form[lvl.level] || ""),
    }));
  };

  const handleSelect = (lvl, idx, value) => {
    setForm((prev) => ({ ...prev, [lvl.level]: value }));
    setTouched((prev) => ({ ...prev, [lvl.level]: true }));
    setErrors((prev) => ({
      ...prev,
      [lvl.level]: getFieldValidationError(lvl, value),
    }));
  };

  const handleSave = async () => {
    // Client-side required fields check
    const allTouched = {};
    const freshErrors = {};
    locationLevels.forEach((lvl) => {
      allTouched[lvl.level] = true;
      freshErrors[lvl.level] = getFieldValidationError(lvl, form[lvl.level] || "");
    });
    setTouched(allTouched);
    setErrors(freshErrors);
    setFormError("");
    if (Object.values(freshErrors).some(Boolean)) return;

    setSaving(true);
    try {
      const res = isEdit
        ? await updateOrgLocationMaster(location.id, form)
        : await createOrgLocationMaster(form);

      if (res?.responseStatus?.code === CODE.SUCCESS || res?.responseStatus?.code === 201) {
        openNotification({
          text: isEdit ? "Location updated successfully" : "Location created successfully",
          icon: "toast-success",
        });
        onSuccess();
      } else {
        const msg = res?.responseStatus?.msg || "";
        const parts = msg.split("||");
        const fieldErrors = {};
        let combinedError = "";

        parts.forEach((part) => {
          if (part.startsWith("FIELD_CONFLICT:")) {
            const [, field, fieldType, value] = part.split(":", 4);
            fieldErrors[field] = `${fieldType} "${value}" already exists`;
          } else if (part === "COMBINED_CONFLICT") {
            combinedError = "This exact location combination already exists in the system.";
          }
        });

        if (combinedError) {
          setFormError(combinedError);
        } else if (Object.keys(fieldErrors).length > 0) {
          setErrors((prev) => ({ ...prev, ...fieldErrors }));
        }
        if (Object.keys(fieldErrors).length === 0 && !combinedError) {
          openNotification({
            text: msg || (isEdit ? "Failed to update location" : "Failed to create location"),
            icon: "toast-error",
          });
        }
      }
    } finally {
      setSaving(false);
    }
  };

  const hasValidationError = Object.values(errors).some(Boolean);
  const allFieldsFilled = locationLevels.every((lvl) => (form[lvl.level] || "").trim());
  const isSaveDisabled = saving || !allFieldsFilled || hasValidationError;
  const isOddCount = locationLevels.length % 2 !== 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Grow}
      TransitionProps={{ timeout: 200 }}
      maxWidth={false}
      slotProps={{
        backdrop: { sx: { backdropFilter: "blur(5px)", backgroundColor: "rgba(15,23,42,0.4)" } },
      }}
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: 740,
          borderRadius: "12px",
          overflow: "visible", // suggestion panels must not be clipped
          boxShadow:
            "0 4px 12px rgba(0,0,0,0.06), 0 12px 32px rgba(0,0,0,0.08), 0 32px 80px rgba(0,0,0,0.09)",
          "@media (max-width: 600px)": { maxWidth: "calc(100vw - 32px)", mx: "16px" },
        },
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{ px: 3.5, pt: 3.5, pb: 2.5, borderRadius: "12px 12px 0 0", backgroundColor: "#fff" }}
      >
        <Typography
          sx={{
            fontSize: 18,
            fontWeight: 700,
            color: "#111827",
            lineHeight: "26px",
            fontFamily: "Roboto",
            mb: "5px",
          }}
        >
          {isEdit ? "Edit Location" : "Add Location"}
        </Typography>
        <Typography
          sx={{ fontSize: 13, color: "#94A3B8", lineHeight: "20px", fontFamily: "Roboto" }}
        >
          {isEdit
            ? "Update supported location hierarchy information."
            : "Fill the below details to add a new location to the hierarchy."}
        </Typography>
      </Box>

      {/* ── Form ── */}
      <DialogContent
        sx={{
          px: 3.5,
          pt: 0.5,
          pb: 4,
          backgroundColor: "#fff",
          overflow: "visible", // allow suggestion panels to overflow
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
            gap: "18px",
          }}
        >
          {locationLevels.map((lvl, idx) => {
            const spanFull = isOddCount && idx === locationLevels.length - 1;
            return (
              <Box
                key={lvl.level}
                sx={{ gridColumn: spanFull ? { xs: "1", sm: "1 / -1" } : "auto" }}
              >
                <FormField
                  label={lvl.type}
                  value={form[lvl.level] || ""}
                  onChange={(e) => handleChange(lvl, idx, e.target.value)}
                  onBlur={() => handleBlur(lvl, idx)}
                  onSelect={(val) => handleSelect(lvl, idx, val)}
                  required={true}
                  placeholder={getPlaceholder(lvl.type)}
                  autoFocus={idx === 0}
                  error={touched[lvl.level] ? !!errors[lvl.level] : false}
                  helperText={touched[lvl.level] ? errors[lvl.level] : ""}
                  enableSuggestions={!lvl.isUnique}
                  suggestions={getFieldSuggestions(lvl, form[lvl.level] || "")}
                  exactMatchExists={fieldHasExactMatch(lvl, form[lvl.level] || "")}
                />
              </Box>
            );
          })}
        </Box>
      </DialogContent>

      {/* ── Footer ── */}
      <Box
        sx={{ borderTop: "1.5px solid #EAECF0", borderRadius: "0 0 12px 12px", backgroundColor: "#fff" }}
      >
        {formError && (
          <Box
            sx={{
              mx: 3.5,
              mt: 2,
              px: 2,
              py: 1.5,
              backgroundColor: "#FEF2F2",
              border: "1px solid #FECACA",
              borderRadius: "8px",
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
            }}
          >
            <Typography sx={{ fontSize: 12.5, color: "#B91C1C", lineHeight: "18px", fontFamily: "Roboto" }}>
              {formError}
            </Typography>
          </Box>
        )}
        <DialogActions sx={{ px: 3.5, pt: 2.5, pb: 3.5, gap: 1.5, justifyContent: "flex-end" }}>
          <CancelFormActionButton
            onClick={onClose}
            disabled={saving}
            sx={{
              border: "1.5px solid #E5E7EB",
              color: "#374151",
              transition: "all 0.18s ease",
              "&:hover": { backgroundColor: "#F9FAFB", borderColor: "#D1D5DB", color: "#111827" },
            }}
          >
            Cancel
          </CancelFormActionButton>
          <PrimaryFormActionButton
            onClick={handleSave}
            disabled={isSaveDisabled}
            sx={{
              minWidth: 130,
              boxShadow: isSaveDisabled
                ? "none"
                : "0 1px 3px rgba(109,114,246,0.18), 0 4px 14px rgba(109,114,246,0.18)",
              transition: "all 0.18s ease",
              "&:hover": {
                boxShadow: "0 2px 6px rgba(109,114,246,0.28), 0 8px 24px rgba(109,114,246,0.22)",
                transform: "translateY(-1px)",
              },
              "&:active": {
                transform: "translateY(0px)",
                boxShadow: "0 1px 3px rgba(109,114,246,0.18)",
              },
              "&.Mui-disabled": { boxShadow: "none", transform: "none" },
            }}
          >
            {saving ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={15} sx={{ color: "#fff" }} />
                {isEdit ? "Saving..." : "Creating..."}
              </Box>
            ) : isEdit ? (
              "Save Changes"
            ) : (
              "Create Location"
            )}
          </PrimaryFormActionButton>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default CreateEditLocationDialog;
